import { useEffect, useState } from "react";
import { Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { PageTitle } from "@/components/Brand";
import { pick } from "@/i18n";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";

export function ProposalCards({ lang, t, proposals, selected, onSelect, actionLabel }) {
  return (
    <div className="proposals" data-testid="proposals">
      {proposals.map((p) => (
        <div className={`proposal ${p.recommended ? "recommended" : ""} ${selected === p.tier ? "selected" : ""}`} key={p.tier} data-testid={`proposal-${p.tier.toLowerCase()}`}>
          {p.recommended && <span className="ribbon">{t.recommended}</span>}
          <p className="eyebrow">{p.tier === "Strategic" ? "Premium" : p.tier}</p>
          <h3>{p.tier === "Strategic" ? (lang === "fa" ? "ویژه" : "Premium") : pick(p.label, lang)}</h3>
          <div className="price"><strong>{(p.price / 1000000).toLocaleString(lang === "fa" ? "fa-IR" : "en-US")} {lang === "fa" ? "میلیون تومان" : "million toman"}</strong><span>{t.oneTime}</span></div>
          <p className="muted">{pick(p.summary, lang)}</p>
          <ul>{pick(p.includes, lang).map((x) => <li key={x}><Check size={13} />{x}</li>)}</ul>
          <button className={p.recommended ? "primary-button full" : "quiet-button full"} onClick={() => onSelect(p)} data-testid={`proposal-choose-${p.tier.toLowerCase()}`}>{actionLabel}</button>
        </div>
      ))}
    </div>
  );
}

export default function Services({ lang, t, setSection }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => { api.get("/services").then((r) => setData(r.data)).catch(() => setData({ services: [], proposals: [], requests: [] })); }, []);

  const request = async (id) => {
    try {
      const r = await api.post("/services/request", { service_id: id, note, lang });
      setData((d) => ({ ...d, requests: [r.data, ...d.requests] })); setActive(null); setNote(""); toast.success(t.received);
    } catch (err) { toast.error(errorText(err, "Could not send request.")); }
  };
  const choose = (p) => { sessionStorage.setItem("noorin-tier", p.tier); setSection("subscription"); };

  if (!data) return <p className="muted">{t.loading}</p>;
  const requestedIds = new Set(data.requests.map((r) => r.service_id));

  return (
    <div className="section-page wide">
      <PageTitle eyebrow={t.eyebrows.services} title={t.services} sub={t.servicesSub} />
      <StrategyRoomCTA t={t} testId="services-strategy-room" />
      <div className="services-grid" data-testid="services-list">
        {data.services.map((s) => (
          <div className={`service-card ${active === s.id ? "active" : ""}`} key={s.id} data-testid={`service-${s.id}`}>
            <ShieldCheck size={18} />
            <h3>{pick(s.title, lang)}</h3>
            <p>{pick(s.detail, lang)}</p>
            {active === s.id ? (
              <div className="stack-form">
                <textarea rows="3" placeholder={t.requestNote} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`service-note-${s.id}`} />
                <div className="row-actions"><button className="primary-button" onClick={() => request(s.id)} data-testid={`service-send-${s.id}`}>{t.sendRequest}</button><button className="quiet-button" onClick={() => setActive(null)} data-testid={`service-cancel-${s.id}`}>{t.cancel}</button></div>
              </div>
            ) : (
              <button className="quiet-button" onClick={() => { setActive(s.id); setNote(""); }} data-testid={`service-request-${s.id}`}>{requestedIds.has(s.id) ? `✓ ${t.requested}` : t.requestService}</button>
            )}
          </div>
        ))}
      </div>
      <div className="section-heading proposals-heading"><div><p className="eyebrow">{t.proposals}</p><h2>{t.proposalsSub}</h2></div></div>
      <ProposalCards lang={lang} t={t} proposals={data.proposals} onSelect={choose} actionLabel={t.choose} />
      {data.requests.length > 0 && (
        <div className="list requests" data-testid="service-requests">
          <p className="eyebrow">{t.yourRequests}</p>
          {data.requests.map((r) => <div className="list-item" key={r.id}><div className="list-row"><div><strong>{pick(data.services.find((s) => s.id === r.service_id)?.title, lang)}</strong>{r.note && <p>{r.note}</p>}</div><span className="tag">{t.received}</span></div></div>)}
        </div>
      )}
    </div>
  );
}
